import React from 'react'
import OdxIcon from '../Odx-Icon'
import {OdxInputError} from './Interfaces'

interface OdxInputFieldProps {
    placeholder?: string
    isRequired: boolean
    index: string
    label: string
    error?: OdxInputError
    odxIcon?: React.ReactElement<typeof OdxIcon>
    justifyIcon?: 'start' | 'end'
    value?: string
    type?: string
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void,
    onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void
    className?: string
    isDisabled?: boolean
    hint?: string
    hidden?: boolean
}

/** Usage
 * <OdxInputField
 *                 label={'First Name'}
 *                 value={inputValue}
 *                 justifyIcon={'start'}
 *                 onChange={(event) => setInputValue(event.target.value)}
 *                 odxIcon={<OdxIcon iconSet={odxIconSets.core} size={'sm'} name={user} />}
 *                 index={'inputField'}
 *                 error={{ text: 'This field needs to be more then 3 characters long', isActive: validation(inputValue) }}
 *                 isRequired={true}
 *               />
 */

/**
 * Formfield field component. Is used to display a form field.
 * @param placeholder placeholder text to display in the input field when no value is entered
 * @param index unique index for the input field is used for filling the id and references
 * @param label label to display above the input field
 * @param isRequired if true, the input field will be marked as required (optional)
 * @param value value of the input field
 * @param onChange function to call when the value of the input field changes
 * @param onBlur
 * @param error error to display below the input field (optional)
 * @param justifyIcon position of the icon in the input field (optional)
 * @param odxIcon icon to display in the input field (optional)
 * @param type type of the input field
 * @param className
 * @param isDisabled
 * @param hint
 * @param hidden if true, the input field content will be hidden (optional)
 */

const OdxFormfield = ({
                          placeholder,
                          index,
                          label,
                          isRequired = false,
                          odxIcon,
                          value,
                          onChange,
                          onBlur,
                          error,
                          justifyIcon = 'start',
                          className,
                          isDisabled = false,
                          hint = ''
                      }: OdxInputFieldProps) => {
    return (
        <div
            className={`odx-form-field--full odx-form-field ${isRequired ? 'odx-form-field--required' : ''} ${error?.isActive ? 'has-error' : ''} ${className ? className : ''}`}
        >
            <label className='odx-form-field__label' htmlFor={'odx-form-field-' + index}
                   aria-owns={'odx-form-field-' + index}>
                <span className='odx-form-field-label'>{label}</span>
                {isRequired && <span className='odx-form-field__label-is-required'>*</span>}
            </label>
            <div className='odx-form-field__inner'>
                <div className='odx-form-field__control'>
                    {justifyIcon === 'start' && <div className='odx-form-field__prefix'>{odxIcon}</div>}

                    <textarea
                        key={index}
                        disabled={isDisabled}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder={placeholder}
                        className='odx-form-field-control'
                        aria-required='true'
                        id={'odx-form-field-' + index}
                        aria-describedby={'odx-form-field-' + index + '-hint'}
                        aria-invalid={error?.isActive ? 'true' : 'false'}
                        aria-errormessage={'odx-form-field-' + index + '-error'}
                    />

                    {justifyIcon === 'end' && <div className='odx-form-field__suffix'>{odxIcon}</div>}
                </div>
            </div>

            {(!hint && !error) || error?.isActive &&
                <div className='odx-form-field__info'>
                <span className='odx-form-field-error' id={'odx-form-field-' + index + '-error'}>
                    {error && error.isActive && error.text}
                </span>
                </div>
            }

            {hint && (
                <div className='odx-form-field__info'>
                    <span className='odx-form-field-hint' id={'odx-form-field-' + index + '-hint'}>
                        {hint}
                    </span>
                </div>
            )}
        </div>
    )
}

export default OdxFormfield